/**
 * src/components/mobile/MobileAssets.jsx
 * Route: /mobile/assets
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MobileLayout from './MobileLayout';

const API     = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const IMG_BASE = API.replace('/api', '');
const auth    = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

const STATUS = {
  operational: { label: 'operational', bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  maintenance:  { label: 'maintenance',  bg: '#fef3c7', color: '#d97706', border: '#fde68a' },
  breakdown:    { label: 'breakdown',    bg: '#fff1f2', color: '#dc2626', border: '#fecaca' },
  retired:      { label: 'retired',      bg: '#f8fafc', color: '#94a3b8', border: '#e2e8f0' },
};
const stCfg = s => STATUS[(s||'').toLowerCase()] || STATUS.operational;

export default function MobileAssets() {
  const navigate = useNavigate();
  const [assets,   setAssets]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('All');
  const [locations, setLocations] = useState(['All']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assets/?limit=200`, { headers: auth() });
      const list = r.data?.data || r.data || [];
      setAssets(list);
      const locs = ['All', ...new Set(list.map(a => a.location_name).filter(Boolean))];
      setLocations(locs);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Group by location */
  const filtered = filter === 'All'
    ? assets
    : assets.filter(a => a.location_name === filter);

  const byLocation = filtered.reduce((acc, a) => {
    const loc = a.location_name || 'No Location';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(a);
    return acc;
  }, {});

  return (
    <MobileLayout active="assets" showBack title="Assets">
      {/* Filter bar */}
      <div style={{ padding: '10px 14px', background: '#fff',
        borderBottom: '1px solid #f1f5f9', display:'flex', gap:8 }}>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0',
            borderRadius: 8, fontSize: 13, color: '#1a1f2e',
            background: '#fff', flex: 1, outline: 'none' }}>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:13 }}>
          Loading assets…
        </div>
      ) : (
        <div style={{ padding: '8px 0 8px' }}>
          {Object.entries(byLocation).map(([loc, list]) => (
            <div key={loc}>
              {/* Location header */}
              <div style={{ padding: '8px 14px 4px', display:'flex',
                alignItems:'center', gap:6 }}>
                <span style={{ fontSize:11, color:'#94a3b8' }}>⊙</span>
                <span style={{ fontSize:12, fontWeight:600, color:'#64748b',
                  textTransform:'uppercase', letterSpacing:'.06em' }}>{loc}</span>
              </div>

              {/* Asset cards */}
              {list.map(asset => {
                const st   = stCfg(asset.current_status || asset.status);
                const imgSrc = asset.image_url
                  ? (asset.image_url.startsWith('http') ? asset.image_url : `${IMG_BASE}${asset.image_url}`)
                  : (asset.image
                      ? (asset.image.startsWith('http') ? asset.image : `${IMG_BASE}${asset.image}`)
                      : null);

                return (
                  <div key={asset.id}
                    onClick={() => navigate(`/a/${asset.asset_code}`)}
                    style={{ margin: '0 0 2px', padding: '12px 14px',
                      background: '#fff', borderBottom: '1px solid #f1f5f9',
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      cursor: 'pointer', WebkitTapHighlightColor:'transparent' }}>

                    {/* Left content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Row 1: star + code + badge */}
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                        <span style={{ color:'#f59e0b', fontSize:14, flexShrink:0 }}>★</span>
                        <span style={{ fontSize:12, fontWeight:700, color:'#1a56db',
                          fontFamily:'monospace' }}>{asset.asset_code}</span>
                        <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px',
                          borderRadius:10, background:st.bg, color:st.color,
                          border:`1px solid ${st.border}` }}>
                          {st.label}
                        </span>
                      </div>
                      {/* Asset name */}
                      <div style={{ fontSize:14, fontWeight:700, color:'#1a1f2e',
                        lineHeight:1.3, marginBottom:3 }}>
                        {asset.name}
                      </div>
                      {/* Category · Serial */}
                      <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>
                        {[asset.category_name, asset.serial_no].filter(Boolean).join(' · ')}
                      </div>
                      {/* Location */}
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <span style={{ fontSize:11, color:'#94a3b8' }}>📍</span>
                        <span style={{ fontSize:11, color:'#94a3b8' }}>{asset.location_name || '—'}</span>
                      </div>
                    </div>

                    {/* Right: photo */}
                    <div style={{ width:64, height:64, flexShrink:0,
                      background:'#f1f5f9', borderRadius:8, overflow:'hidden',
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {imgSrc ? (
                        <img src={imgSrc} alt={asset.name}
                          style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      ) : (
                        <span style={{ fontSize:9, color:'#94a3b8', textAlign:'center',
                          lineHeight:1.3, padding:4 }}>Asset<br/>Photo</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {Object.keys(byLocation).length === 0 && (
            <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:13 }}>
              No assets found.
            </div>
          )}
        </div>
      )}
    </MobileLayout>
  );
}
